import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminPayouts,
  approveAdminPayout,
  rejectAdminPayout,
  markAdminPayoutPaid,
  getAdminAgentPayoutDetail
} from "../../../redux/actions/profile";

const BG = "#2c2d3f";

const STATUS_COLOR = {
  pending: "#f4a261",
  approved: "#4fb0c6",
  paid: "#2ec4b6",
  rejected: "#e63946"
};

const TIER_LABEL = (n) => {
  if (n >= 1000) return "15% (1000+ merchants)";
  if (n >= 100)  return "12% (100–999 merchants)";
  return "10% (1–99 merchants)";
};

export default function PayoutManagement() {
  const dispatch = useDispatch();
  const payouts = useSelector((s) => s.profile.adminPayouts);
  const agentDetail = useSelector((s) => s.profile.adminAgentPayoutDetail);

  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null); // "approve" | "reject" | "detail"
  const [approveForm, setApproveForm] = useState({ approvedAmount: "", note: "" });
  const [rejectForm, setRejectForm] = useState({ rejectionReason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = (p = page) => {
    const params = { page: p, limit: 50 };
    if (status) params.status = status;
    dispatch(getAdminPayouts(params));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [dispatch]);

  const openApprove = (p) => {
    setSelected(p);
    setApproveForm({ approvedAmount: p.requestedAmount, note: "" });
    setMsg(null);
    setModal("approve");
  };

  const openReject = (p) => {
    setSelected(p);
    setRejectForm({ rejectionReason: "" });
    setMsg(null);
    setModal("reject");
  };

  const openDetail = async (p) => {
    setSelected(p);
    setModal("detail");
    if (p.agentId?._id) {
      dispatch(getAdminAgentPayoutDetail(p.agentId._id));
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    setSubmitting(true); setMsg(null);
    const res = await dispatch(approveAdminPayout(selected._id, {
      approvedAmount: Number(approveForm.approvedAmount),
      note: approveForm.note
    }));
    setSubmitting(false);
    if (res?.success) { setMsg({ type: "success", text: "Payout approved" }); load(); setTimeout(() => setModal(null), 700); }
    else setMsg({ type: "error", text: res?.message || "Failed" });
  };

  const handleReject = async () => {
    if (!selected) return;
    setSubmitting(true); setMsg(null);
    const res = await dispatch(rejectAdminPayout(selected._id, { rejectionReason: rejectForm.rejectionReason }));
    setSubmitting(false);
    if (res?.success) { setMsg({ type: "success", text: "Payout rejected" }); load(); setTimeout(() => setModal(null), 700); }
    else setMsg({ type: "error", text: res?.message || "Failed" });
  };

  const handleMarkPaid = async (p) => {
    const res = await dispatch(markAdminPayoutPaid(p._id));
    if (res?.success) load();
  };

  const fmtMoney = (n) => n != null ? `₦${Number(n).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const pendingCount = payouts.filter((p) => p.status === "pending").length;

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>Payout Requests</h2>
          <p style={S.headingSub}>Review and approve agent commission withdrawals</p>
        </div>
        {pendingCount > 0 && (
          <div style={S.alertPill}>
            <i className="bi bi-bell-fill" style={{ marginRight: 6 }} />
            {pendingCount} pending
          </div>
        )}
      </div>

      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>Status</label>
          <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
          <button style={S.btn} onClick={() => { setPage(1); load(1); }}>Filter</button>
          <button style={S.btnSecondary} onClick={() => { setStatus(""); setPage(1); dispatch(getAdminPayouts({ page: 1, limit: 50 })); }}>Reset</button>
        </div>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Agent", "Requested", "Bank", "Status", "Date", "Actions"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payouts.length === 0 ? (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No payout requests found</td></tr>
            ) : payouts.map((p) => (
              <tr key={p._id} style={S.tr}>
                <td style={S.td}>
                  <div style={{ fontWeight: 600, color: BG }}>{p.agentId?.fullName || "—"}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>{p.agentEmail}</div>
                  {p.agentId && (
                    <div style={{ fontSize: 11, color: "#bbb" }}>
                      {p.agentId.merchants?.length || 0} merchants · {TIER_LABEL(p.agentId.merchants?.length || 0)}
                    </div>
                  )}
                </td>
                <td style={{ ...S.td, fontWeight: 700, color: BG }}>
                  {fmtMoney(p.requestedAmount)}
                  {p.approvedAmount && p.approvedAmount !== p.requestedAmount && (
                    <div style={{ fontSize: 11, color: "#4fb0c6" }}>Approved: {fmtMoney(p.approvedAmount)}</div>
                  )}
                  {p.agentBalance && (
                    <div style={{ fontSize: 11, color: "#888" }}>Balance: {fmtMoney(p.agentBalance.availableBalance)}</div>
                  )}
                </td>
                <td style={S.td}>
                  <div style={{ fontSize: 12 }}>{p.bankDetails?.accountName || "—"}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{p.bankDetails?.bankName} · {p.bankDetails?.accountNumber}</div>
                </td>
                <td style={S.td}>
                  <span style={{ background: (STATUS_COLOR[p.status] || "#888") + "18", color: STATUS_COLOR[p.status] || "#888", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                    {p.status}
                  </span>
                  {p.rejectionReason && <div style={{ fontSize: 11, color: "#e63946", marginTop: 2 }}>{p.rejectionReason}</div>}
                </td>
                <td style={{ ...S.td, color: "#888" }}>{fmtDate(p.createdAt)}</td>
                <td style={S.td}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button style={S.actionBtn} onClick={() => openDetail(p)}>
                      <i className="bi bi-eye" />
                    </button>
                    {p.status === "pending" && (
                      <>
                        <button style={{ ...S.actionBtn, color: "#2ec4b6", borderColor: "#2ec4b6" }} onClick={() => openApprove(p)}>Approve</button>
                        <button style={{ ...S.actionBtn, color: "#e63946", borderColor: "#e63946" }} onClick={() => openReject(p)}>Reject</button>
                      </>
                    )}
                    {p.status === "approved" && (
                      <button style={{ ...S.actionBtn, color: "#9b5de5", borderColor: "#9b5de5" }} onClick={() => handleMarkPaid(p)}>Mark Paid</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.pagination}>
        <button style={S.pgBtn} disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p); }}>‹ Previous</button>
        <span style={{ color: "#888", fontSize: 13 }}>Page {page}</span>
        <button style={S.pgBtn} onClick={() => { const p = page + 1; setPage(p); load(p); }}>Next ›</button>
      </div>

      {/* Approve modal */}
      {modal === "approve" && selected && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>Approve Payout</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                  {selected.agentId?.fullName} — requested {fmtMoney(selected.requestedAmount)}
                </div>
              </div>
              <button style={S.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              <label style={S.label}>Approved Amount (₦)</label>
              <input style={S.input} type="number" value={approveForm.approvedAmount} onChange={(e) => setApproveForm({ ...approveForm, approvedAmount: e.target.value })} />
              <label style={S.label}>Note (optional)</label>
              <input style={S.input} placeholder="e.g. transferred via bank" value={approveForm.note} onChange={(e) => setApproveForm({ ...approveForm, note: e.target.value })} />
              {msg && <div style={S.msgBox(msg.type)}>{msg.text}</div>}
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button style={{ ...S.btn, background: "#2ec4b6" }} onClick={handleApprove} disabled={submitting}>{submitting ? "…" : "Confirm Approval"}</button>
                <button style={S.btnSecondary} onClick={() => setModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {modal === "reject" && selected && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>Reject Payout</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{selected.agentEmail}</div>
              </div>
              <button style={S.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              <label style={S.label}>Reason for rejection *</label>
              <input style={S.input} placeholder="e.g. Bank details mismatch" value={rejectForm.rejectionReason} onChange={(e) => setRejectForm({ rejectionReason: e.target.value })} />
              {msg && <div style={S.msgBox(msg.type)}>{msg.text}</div>}
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button style={{ ...S.btn, background: "#e63946" }} onClick={handleReject} disabled={submitting}>{submitting ? "…" : "Reject Payout"}</button>
                <button style={S.btnSecondary} onClick={() => setModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent detail modal */}
      {modal === "detail" && agentDetail && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={{ ...S.modal, width: 600 }} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>{agentDetail.agent.fullName}</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                  {agentDetail.agent.agentCode} · {agentDetail.agent.totalMerchants} merchants · {agentDetail.agent.currentRate} commission rate
                </div>
              </div>
              <button style={S.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Total Earned", value: fmtMoney(agentDetail.balance.totalEarned) },
                  { label: "Total Withdrawn", value: fmtMoney(agentDetail.balance.totalWithdrawn) },
                  { label: "Available Balance", value: fmtMoney(agentDetail.balance.availableBalance) },
                ].map((s) => (
                  <div key={s.label} style={{ background: "#f9f9f9", borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: BG }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 13, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Recent Commission History</div>
              <div style={{ maxHeight: 200, overflowY: "auto", borderRadius: 8, border: "1px solid #f0f0f0" }}>
                {(agentDetail.commissions || []).slice(0, 20).map((c, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: "1px solid #f5f5f5" }}>
                    <div>
                      <span style={{ fontSize: 12, color: BG }}>{c.merchantEmail}</span>
                      <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>{c.plan} · {c.isRenewal ? "renewal" : "new"}</span>
                    </div>
                    <span style={{ fontWeight: 600, color: "#2ec4b6", fontSize: 13 }}>{fmtMoney(c.amount)} ({(c.rate * 100).toFixed(0)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const msgBox = (type) => ({ borderRadius: 8, padding: "10px 14px", fontSize: 13, marginTop: 12, background: type === "success" ? "rgba(46,196,182,0.1)" : "rgba(230,57,70,0.08)", color: type === "success" ? "#2ec4b6" : "#e63946", border: `1px solid ${type === "success" ? "#2ec4b6" : "#e63946"}` });

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  heading: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  alertPill: { background: "rgba(244,162,97,0.12)", border: "1px solid #f4a261", borderRadius: 10, padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "#f4a261", display: "flex", alignItems: "center" },
  filterBar: { background: "#fff", borderRadius: 10, padding: "16px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 20, alignItems: "flex-end" },
  filterGroup: { display: "flex", flexDirection: "column", gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  select: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  btn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "8px 20px", cursor: "pointer", fontSize: 13 },
  btnSecondary: { background: "#f5f5f5", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#666", fontWeight: 500, padding: "8px 16px", cursor: "pointer", fontSize: 13 },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  actionBtn: { background: "#f9f9f9", border: "1.5px solid #e0e0e0", borderRadius: 6, color: "#555", padding: "4px 10px", cursor: "pointer", fontSize: 12, fontWeight: 500 },
  pagination: { display: "flex", alignItems: "center", gap: 16, marginTop: 16, justifyContent: "flex-end" },
  pgBtn: { background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 18px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal: { background: "#fff", borderRadius: 12, width: 460, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" },
  modalHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid #f0f0f0" },
  modalTitle: { fontSize: 16, fontWeight: 700, color: BG },
  closeBtn: { background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", lineHeight: 1 },
  modalBody: { padding: "20px 24px 28px" },
  label: { display: "block", color: "#555", fontSize: 13, fontWeight: 600, marginBottom: 6, marginTop: 12 },
  input: { width: "100%", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "9px 12px", fontSize: 13, color: "#333", background: "#fafafa", boxSizing: "border-box", outline: "none" },
  msgBox
};
